import {Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {ActivatedRoute, NavigationStart, Router} from '@angular/router';
import {Location} from '@angular/common';
import {auditTime, debounceTime, delay, filter, finalize, share} from 'rxjs/operators';
import {Subject, Subscription, timer} from 'rxjs';

import {
  CONTAINER_SCROLL_DELAY,
  DETAIL_POPUP_DELAY,
  HISTORY_KEY_SEARCH_EXAMPLE,
  LIST_TYPE,
  REPLACE_URL_AUDIT_TIME,
  SCROLL_DEBOUNCE_TIME,
  SearchParam,
  triggerDetailPopup,
  UI_RELOAD_HEIGHT,
  EXAMPLE_DETAIL_HEIGHT_DELTA
} from '../shared/defines/client.const';
import {generateUrlWithQueryParams, getDetailHeight, scrollToNode} from '../shared/defines/client.func';
import {LayoutMgrService} from '../shared/services/layout-mgr/layout-mgr.service';
import {ListLoader} from '../shared/interfaces/list-loader';
import {Example} from '../shared/models/example.model';
import {ExampleService} from '../shared/services/example/example.service';
import {FooterComponent} from '../shared/components/footer/footer.component';
import {ExampleHeaderComponent} from './example-header/example-header.component';
import {ExampleListItemComponent} from './example-list-item/example-list-item.component';
import {VoiceService} from '../shared/services/voice/voice.service';
import {IsEqualObj1} from '../shared/defines/common.func';
import {NormalizeSearchParamKeyword} from '../shared/defines/search-keyword.func';

const THIS_PAGE_ADDR = '/example';
const EXAMPLE_SELECT_ID = 'example_id_';

function exampleSelectId(example: Example): string {
  return EXAMPLE_SELECT_ID + example.id;
}

@Component({
  selector: 'app-example',
  templateUrl: './example.component.html',
  styleUrls: ['./example.component.scss'],
  animations: [triggerDetailPopup],
})
export class ExampleComponent implements OnInit, OnDestroy {
  @ViewChild('container') container: ElementRef;
  @ViewChild('reload') reload: ElementRef;
  @ViewChild(ExampleHeaderComponent) header: ExampleHeaderComponent;
  @ViewChild(FooterComponent) footer: FooterComponent;
  @ViewChildren(ExampleListItemComponent) listItems: QueryList<ExampleListItemComponent>;

  readonly HISTORY_KEY_SEARCH_EXAMPLE = HISTORY_KEY_SEARCH_EXAMPLE;
  readonly exampleSelectId = exampleSelectId;

  private type: LIST_TYPE = null;
  private queryParams: SearchParam = null;
  private queryParams$: Subscription = null;
  private routeParams$: Subscription = null;
  private getNextList$: Subscription = null;
  private inited = false;
  private needReload = false;

  // 라우팅 처리
  private router$?: Subscription | null = null;

  // 라우팅 컨트롤 변수
  private scrollEvent$: Subject<void>;
  private scrollEvent$$: Subscription;
  private replaceUrl$: Subject<string>;
  private replaceUrl$$: Subscription;

  public ll: ListLoader<Example>;
  public keyword: string = null;
  public curDetail: Example = null;
  public noResultTxt = '예문이 없습니다.';

  // 상세화면 표시 관련 정의
  public showDetail = false;
  public detailHeight = 0;
  private oldScrollTop: number;
  private get deltaHeight(): number {
    return EXAMPLE_DETAIL_HEIGHT_DELTA;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private exampleSvc: ExampleService,
    private voice: VoiceService,
    public lm: LayoutMgrService,
  ) {
    this.queryParams$ = this.route.queryParams
      .subscribe(params => {
        const oldParams = {...this.queryParams};
        this.queryParams = NormalizeSearchParamKeyword({...params});
        delete this.queryParams.serial;

        this.type = this.queryParams?.l ?? LIST_TYPE.SERVER;
        this.keyword = this.queryParams?.keyword ?? null;

        if (this.inited) {
          if (this.isSameListQuery(oldParams, this.queryParams)) {
            if (this.queryParams?.id) {
              this.openDetailByQuery();
            } else {
              this.CloseDetail();
            }
          } else {
            this.init();
          }
        }
      });

    this.routeParams$ = this.route.params.subscribe(() => {
      this.inited = true;
      this.init();
    });

    this.resetScroll();

    this.lm.Reset();
  }

  ngOnInit(): void {
    this.router$ = this.router.events.pipe(
      filter(event => event instanceof NavigationStart),
    ).subscribe((event) => {
      const curBasePath = this.router.url.split('?')[0];
      const tarBasePath = (event as NavigationStart).url.split('?')[0];
      if (curBasePath !== tarBasePath) {
        this.onLeave();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.getNextList$) {
      this.getNextList$.unsubscribe();
      this.getNextList$ = null;
    }
    if (this.queryParams$) {
      this.queryParams$.unsubscribe();
      this.queryParams$ = null;
    }
    if (this.routeParams$) {
      this.routeParams$.unsubscribe();
      this.routeParams$ = null;
    }
    if (this.router$) {
      this.router$.unsubscribe();
      this.router$ = null;
    }
    if (this.scrollEvent$$) {
      this.scrollEvent$$.unsubscribe();
      this.scrollEvent$$ = null;
    }
    if (this.replaceUrl$$) {
      this.replaceUrl$$.unsubscribe();
      this.replaceUrl$$ = null;
    }
    if (this.showDetail) {
      this.CloseDetail();
    }

    this.clearSeungjo();
  }

  private clearSeungjo(): void {
    this.listItems?.forEach(item => item.ClearSeungjo());
  }

  private init(): void {
    console.log('ExampleComponent::init()', this.type, this.queryParams);

    if (this.curDetail) {
      this.CloseDetail();
    }

    switch (this.type) {
      case LIST_TYPE.SERVER:
        this.ll = this.exampleSvc;
        break;
      default:
        console.warn(`ExampleComponent::init() unsupported list type "${this.type}", falling back to SERVER`);
        this.ll = this.exampleSvc;
        this.type = LIST_TYPE.SERVER;
        break;
    }

    this.ReloadList(this.queryParams ?? {});
  }

  /**
   * 이 함수는 라우터의 base 주소가 변경될 때 불려진다.
   * @private
   */
  private onLeave(): void {
    console.warn('ExampleComponent::onLeave()', this.queryParams);

    if (this.queryParams && 'keyword' in this.queryParams) {
      return;
    }

    const top: Example = this.scrollTopTarget;
    if (!top) {
      return;
    }

    const topId = top.id;
    const queryParam: SearchParam = {
      ...(this.queryParams ? this.queryParams : {}),
      start: topId,
    };
    const basePath = this.router.url.split('?')[0];
    const urlWithParams = generateUrlWithQueryParams(basePath, queryParam);
    console.warn('ExampleComponent::replaceUrl$ urlWithParams =', urlWithParams);

    this.location.replaceState(urlWithParams);
  }

  public onSearch(params: SearchParam): void {
    const queryParams: SearchParam = {
      ...NormalizeSearchParamKeyword(params),
      l: LIST_TYPE.SERVER,
    };
    this.router.navigate([THIS_PAGE_ADDR], {queryParams}).then();

    this.resetScroll();
  }

  public ReloadList(param: SearchParam): void {
    const searchParam = NormalizeSearchParamKeyword(param);
    if (this.queryParams?.id && !this.queryParams.start) {
      searchParam.start = this.queryParams.id;
    }

    this.ll.ResetList(searchParam);
    this.loadList(true);

    // 새 조건으로 목록을 재로딩할 때 목록 스크롤을 최상단으로 되돌린다. (#602)
    this.scrollListToTop();
  }

  /**
   * 목록을 새로 로딩할 때 컨테이너 스크롤을 최상단(0)으로 되돌린다. (#602)
   *
   * iOS(Cordova) WKWebView 는 관성(momentum) 스크롤이 진행 중일 때 `scrollTop = 0`
   * 대입을 무시한다. 그래서 목록을 손가락으로 스크롤(=관성 활성)하다가 색인/검색을
   * 바꾸면, 재로딩이 빠르게 끝나 관성이 아직 살아있는 경우 리셋이 덮여 이전 위치가
   * 남는다. (느린 재로딩은 관성 종료 후 리셋되어 정상, 웹·안드로이드는 관성이
   * 프로그램적 스크롤을 덮지 않아 정상.) overflow 를 잠깐 hidden 으로 만들어 관성을
   * 끊고 최상단으로 고정한 뒤 원복한다. word 화면과 동일 대응(#602, PR #603).
   *
   * 이 메서드는 전체 재로딩 경로(`ReloadList()`)에서만 호출되므로 상세 열기/닫기
   * (`id` 변경)나 무한 스크롤 페이지네이션에는 영향을 주지 않는다.
   * @private
   */
  private scrollListToTop(): void {
    const el = this.container?.nativeElement;
    if (!el) {
      return;
    }
    // overflow 를 잠깐 없애 관성 스크롤을 끊고 최상단으로 고정한다. (iOS WKWebView 대응)
    const prevOverflowY = el.style.overflowY;
    el.style.overflowY = 'hidden';
    el.scrollTop = 0;
    void el.offsetHeight;              // 강제 reflow 로 관성 취소를 커밋
    el.style.overflowY = prevOverflowY;
    el.scrollTop = 0;

    // 새 목록 DOM 재렌더 이후에도 최상단을 유지한다. (렌더 타이밍 안전망)
    timer(0).subscribe(() => {
      const c = this.container?.nativeElement;
      if (c) {
        c.scrollTop = 0;
      }
    });
  }

  private loadList(cancel = false): void {
    if (this.getNextList$) {
      console.warn('ExampleComponent::loadList() already call loadList!');
      if (cancel) {
        this.getNextList$.unsubscribe();
      } else {
        return;
      }
    }

    if (this.ll.IsComplete()) {
      console.warn('ExampleComponent::loadList() already load all examples!');
      return;
    }

    const sharedObservable$ = this.ll.GetNextList().pipe(share());

    this.getNextList$ = sharedObservable$.pipe(
      finalize(() => {
        this.getNextList$ = null;
        if (this.needReload) {
          this.needReload = false;
          this.loadList();
        }
      })
    ).subscribe(() => {
      if (this.queryParams?.id) {
        const example = this.ll.getById(this.queryParams.id);
        if (example) {
          this.OpenDetail(example);
        }
      }
    });
  }

  public onScroll($event): void {
    this.clearSeungjo();

    if (!this.ll) {
      return;
    }

    if (!this.ll.IsLoading() && !this.ll.IsComplete()) {
      const tar = $event.target || $event.srcElement || $event.currentTarget;
      if (tar !== this.container.nativeElement) {
        console.error('ExampleComponent::onScroll() - wrong structure!!');
      }

      const elReload = this.reload?.nativeElement;
      if (elReload && elReload.offsetTop < window.innerHeight + tar.scrollTop + UI_RELOAD_HEIGHT) {
        if (this.getNextList$ === null) {
          this.loadList();
        } else {
          this.needReload = true;
        }
      }
    }

    if (!this.showDetail) {
      this.scrollEvent$.next();
    }
  }

  protected onSelectItem($event: MouseEvent, example: Example): void {
    $event.stopPropagation();
    $event.preventDefault();

    if (this.showDetail) {
      console.warn('ExampleComponent::onSelectItem() already opened!');
      return;
    }

    const queryParams: SearchParam = {
      ...this.queryParams,
      id: example.id,
      l: this.type,
    };
    this.router.navigate([THIS_PAGE_ADDR], {queryParams}).then();

    this.resetScroll();
  }

  protected onCloseItem(): void {
    const queryParams = {
      ...this.queryParams,
      l: this.type,
    };
    delete queryParams.id;

    this.router.navigate([THIS_PAGE_ADDR], {queryParams}).then();
  }

  public OpenDetail(example: Example): void {
    if (this.showDetail) {
      console.warn('ExampleComponent::OpenDetail() already opened!');
      return;
    }

    this.showDetail = true;
    this.curDetail = example;

    timer(CONTAINER_SCROLL_DELAY).subscribe(() => {
      const el = document.getElementById(exampleSelectId(example));
      if (el) {
        const elContainer = el.offsetParent as HTMLElement;
        if (elContainer !== this.container.nativeElement) {
          throw Error('HTML structure error');
        }

        const delta = this.deltaHeight;
        this.detailHeight = getDetailHeight(elContainer, el, this.footer.height + delta);
        this.oldScrollTop = scrollToNode(elContainer, el, - delta);
      }
      else {
        console.error(`document.getElementById(${exampleSelectId(example)}) is null`);
      }
    });
  }

  public CloseDetail(): void {
    console.log('ExampleComponent::CloseDetail()');
    this.curDetail = null;
    if (this.lm.showExampleHelp) {
      this.CloseExampleHelp();
    }
    this.voice?.Stop();
    this.showDetail = false;
  }

  public OpenExampleHelp(): void {
    this.voice?.Stop();
    this.lm.showExampleHelp = true;
  }

  public CloseExampleHelp(): void {
    this.lm.showExampleHelp = false;
  }

  private openDetailByQuery(): void {
    const processParams = () => {
      const example = this.ll?.getById(this.queryParams.id);
      if (example) {
        this.CloseDetail();
        timer(DETAIL_POPUP_DELAY).subscribe(() => this.OpenDetail(example));
      } else {
        this.init();
      }
    };

    if (this.ll?.IsLoading()) {
      this.ll.GetNextList().pipe(
        delay(DETAIL_POPUP_DELAY),
      ).subscribe(processParams);
    } else {
      processParams();
    }
  }

  private isSameListQuery(oldParams: SearchParam, newParams: SearchParam): boolean {
    const oldListParams = {...oldParams};
    const newListParams = {...newParams};
    delete oldListParams.id;
    delete newListParams.id;
    return IsEqualObj1(oldListParams, newListParams);
  }

  /****************************************************************************
   * 스크롤시 상단 아이템 정보 얻어오기
   ***************************************************************************/
  private resetScroll(): void {
    if (this.scrollEvent$$) {
      this.scrollEvent$$.unsubscribe();
    }
    if (this.replaceUrl$$) {
      this.replaceUrl$$.unsubscribe();
    }

    this.scrollEvent$ = new Subject<void>();

    /**
     * 일정 시간 기다리는 동안 새로운 값이 들어오면 또 일정 시간을 다시 기다린다.
     * (새로운 값이 들어오면 대기 시간 연장)
     */
    this.scrollEvent$$ = this.scrollEvent$.pipe(
      debounceTime(SCROLL_DEBOUNCE_TIME)
    ).subscribe(() => {
      console.warn('ExampleComponent::scrollEvent$');

      const top: Example = this.scrollTopTarget;
      if (top) {
        this.replaceUrl$.next(top.id);
      }
    });

    this.replaceUrl$ = new Subject<string>();

    /**
     * 일정 시간 동안 기다린 후, 최종 값을 사용함.
     * (새로운 값이 들어와도 대기 시간 연장 안함)
     */
    this.replaceUrl$$ = this.replaceUrl$.pipe(
      auditTime(REPLACE_URL_AUDIT_TIME),
      filter(() => !this.queryParams || !('keyword' in this.queryParams)),
    ).subscribe((id: string) => {
      const queryParam: SearchParam = {
        ...(this.queryParams ? this.queryParams : {}),
        start: id,
      };
      const basePath = this.router.url.split('?')[0];
      const urlWithParams = generateUrlWithQueryParams(basePath, queryParam);
      console.warn('ExampleComponent::replaceUrl$ urlWithParams =', urlWithParams);

      this.location.replaceState(urlWithParams);
    });
  }

  private lastExampleIndex: number | null = null;
  private lastScrollTop: number = 0;

  private get scrollTopTarget(): Example | undefined {
    const elContainer = this.container?.nativeElement;
    if (!elContainer) {
      return;
    }
    const examples = this.ll.getList();
    if (!examples || examples.length === 0) {
      this.lastExampleIndex = null;
      return;
    }

    const currentScrollTop = elContainer.scrollTop + this.header.height;

    let startIndex = this.lastExampleIndex ?? 0;
    if (currentScrollTop > this.lastScrollTop && startIndex < examples.length - 1) {
      startIndex++;
    } else if (currentScrollTop < this.lastScrollTop && startIndex > 0) {
      startIndex--;
    }

    let low = 0;
    let high = examples.length - 1;
    let mid: number;
    let example: Example;

    while (low <= high) {
      mid = Math.floor((low + high) / 2);
      example = examples[mid];

      if (example.offsetTop < currentScrollTop) {
        low = mid + 1;
      } else if (example.offsetTop > currentScrollTop) {
        high = mid - 1;
      } else {
        this.lastExampleIndex = mid;
        this.lastScrollTop = currentScrollTop;
        return example;
      }
    }

    if (low < examples.length) {
      this.lastExampleIndex = low;
      this.lastScrollTop = currentScrollTop;
      return examples[low];
    }

    this.lastScrollTop = currentScrollTop;
    this.lastExampleIndex = null;
  }
}
